import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { focusEl, focusFirst, getInput, installNav, onGamepadsChange, onInputChange, onNavAction, type InputKind, type NavAction } from './engine.ts';

export * from './engine.ts';

export function useInputKind(): InputKind {
  const [k, setK] = useState(getInput());
  useEffect(() => {
    const off = onInputChange(setK);
    return () => void off();
  }, []);
  return k;
}

export function useGamepads() {
  const [pads, setPads] = useState<Gamepad[]>([]);
  useEffect(() => {
    const off = onGamepadsChange((p) => setPads(p.slice()));
    return () => void off();
  }, []);
  return pads;
}

/** Handle a navigation action while mounted. Return true to consume it. */
export function useNavAction(fn: (a: NavAction) => boolean | void, enabled = true) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!enabled) return;
    const off = onNavAction((a) => ref.current(a));
    return () => void off();
  }, [enabled]);
}

// ---------------------------------------------------------------------------
// Controller hints (bottom bar shown only while a gamepad is the active input)
// ---------------------------------------------------------------------------
export interface Hint {
  button: 'A' | 'B' | 'X' | 'Y' | 'LB' | 'RB' | 'LT' | 'RT' | 'Start';
  label: string;
}
const HintsCtx = createContext<(h: Hint[] | null) => void>(() => {});

/** Declare extra hints for the current screen (merged with A/B defaults). */
export function useHints(hints: Hint[] | null) {
  const set = useContext(HintsCtx);
  const key = JSON.stringify(hints);
  useEffect(() => {
    set(hints);
    return () => set(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, set]);
}

function Glyph({ b }: { b: Hint['button'] }) {
  return <span className={`glyph glyph-${b.toLowerCase()}`}>{b}</span>;
}

/** Installs the engine, wires Back to the router and restores focus on page changes. */
export function NavRoot({ children }: { children: ReactNode }) {
  const nav = useNavigate();
  const loc = useLocation();
  const input = useInputKind();
  const [hints, setHints] = useState<Hint[] | null>(null);

  useEffect(() => installNav(), []);

  useEffect(() => {
    const onBack = () => {
      const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
      if (idx > 0) nav(-1);
      else if (loc.pathname !== '/') nav('/');
    };
    window.addEventListener('replay:back', onBack);
    return () => window.removeEventListener('replay:back', onBack);
  }, [nav, loc.pathname]);

  // Remember what was focused on each history entry (by link target or nav id),
  // so going Back returns to the same game instead of the top of the page.
  const navType = useNavigationType();
  const memory = useRef(new Map<string, string>());
  useEffect(() => {
    const onFocus = (e: FocusEvent) => {
      const el = e.target as HTMLElement;
      const id = el.getAttribute?.('data-nav-id') ?? el.getAttribute?.('href');
      if (id && el.closest('main')) memory.current.set(loc.key, id);
    };
    document.addEventListener('focusin', onFocus);
    return () => document.removeEventListener('focusin', onFocus);
  }, [loc.key]);

  // New screen: without a mouse, put focus somewhere sensible once content renders.
  useEffect(() => {
    if (getInput() === 'mouse' || getInput() === 'touch') return;
    const remembered = navType === 'POP' ? memory.current.get(loc.key) : undefined;
    let tries = 0;
    const t = setInterval(() => {
      tries++;
      if (tries > 25) return clearInterval(t);
      if (remembered) {
        const el = Array.from(document.querySelectorAll<HTMLElement>('main [data-nav-id], main a[href]')).find(
          (e) => (e.getAttribute('data-nav-id') ?? e.getAttribute('href')) === remembered,
        );
        if (el) {
          focusEl(el, { center: true });
          return clearInterval(t);
        }
        if (tries < 15) return; // give the list a moment to render
      }
      const el = document.activeElement;
      if (el && el !== document.body && document.contains(el) && el.closest('main,[data-nav-scope]')) return clearInterval(t);
      if (document.querySelector('main [data-autofocus], main a, main button')) {
        focusFirst();
        clearInterval(t);
      }
    }, 60);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.key]);

  const all: Hint[] = [{ button: 'A', label: 'Seleccionar' }, ...(hints ?? []), { button: 'B', label: 'Atrás' }];
  return (
    <HintsCtx.Provider value={setHints}>
      {children}
      {input === 'gamepad' && (
        <div className="hints" aria-hidden="true">
          {all.map((h) => (
            <span key={h.button + h.label}>
              <Glyph b={h.button} /> {h.label}
            </span>
          ))}
        </div>
      )}
    </HintsCtx.Provider>
  );
}
